import {useRef, useState} from 'react'
import {MessageSquare, Paperclip, Calendar, ListChecks} from 'lucide-react'
import type {Task} from '../../types'
import {useApp} from '../../context/AppContext'
import {getPriorityConfig, formatDate, isTaskOverdue} from '../../utils/helpers'
import {AvatarGroup} from '../common/Avatar'
import Badge from '../common/Badge'
import {LabelBadge} from '../common/Badge'

interface KanbanCardProps {
  task: Task
}

export default function KanbanCard({task}: KanbanCardProps) {
  const {dispatch, getUserById, getLabelById, getBoardById, apiMoveTask} = useApp()
  const [moving, setMoving] = useState(false)
  const [moveError, setMoveError] = useState('')
  const movingRef = useRef(false)
  const board = getBoardById(task.boardId)
  const overdue = isTaskOverdue(task)
  const priority = getPriorityConfig(task.priority)
  const assignees = task.assigneeIds.map((id) => getUserById(id)).filter(Boolean)
  const taskLabels = task.labelIds.map((id) => getLabelById(id)).filter(Boolean)

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('taskId', task.id)
    e.dataTransfer.effectAllowed = 'move'
    ;(e.target as HTMLElement).classList.add('dragging')
  }

  const handleDragEnd = (e: React.DragEvent) => {
    ;(e.target as HTMLElement).classList.remove('dragging')
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}
      className='devops-kanban-card bg-white rounded-xl border border-slate-200 p-3.5 cursor-pointer hover:border-brand-200 transition-all duration-200 group active:scale-[0.98]'
      role='group'
      aria-label={`Tarefa: ${task.title}`}
    >
      {taskLabels.length > 0 && (
        <div className='flex flex-wrap gap-1 mb-2'>
          {taskLabels.map((label) => (
            <LabelBadge key={label!.id} name={label!.name} color={label!.color} />
          ))}
        </div>
      )}

      <h4 className='mb-2'>
        <button
          type='button'
          className='w-full text-left text-sm font-medium text-slate-800 leading-snug group-hover:text-brand-600 transition-colors'
          aria-label={`Abrir tarefa ${task.title}`}
        >
          {task.title}
        </button>
      </h4>

      <div className='flex items-center gap-2 flex-wrap mb-3'>
        <Badge className={priority.color} dot={priority.dot}>
          {priority.label}
        </Badge>
        {task.storyPoints > 0 && (
          <span className='inline-flex items-center justify-center w-6 h-6 rounded-md bg-slate-100 text-xs font-semibold text-slate-600'>
            {task.storyPoints}
          </span>
        )}
      </div>

      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div className='flex flex-wrap items-center gap-3 text-slate-400'>
          {task.comments.length > 0 && (
            <span className='flex items-center gap-1 text-xs'>
              <MessageSquare size={13} />
              {task.comments.length}
            </span>
          )}
          {task.attachments.length > 0 && (
            <span className='flex items-center gap-1 text-xs'>
              <Paperclip size={13} />
              {task.attachments.length}
            </span>
          )}
          {task.subtasks.length > 0 && (
            <span className='flex items-center gap-1 text-xs'>
              <ListChecks size={13} />
              <span>
                {task.subtasks.filter((s) => s.completed).length}/{task.subtasks.length}
              </span>
            </span>
          )}
          {task.dueDate && (
            <span
              className={`flex items-center gap-1 text-xs ${
                overdue ? 'text-red-600 font-semibold' : ''
              }`}
              title={overdue ? 'Prazo atrasado' : 'Prazo'}
            >
              <Calendar size={13} />
              {formatDate(task.dueDate)}
            </span>
          )}
        </div>

        {assignees.length === 0 && (
          <span className='text-[11px] text-slate-400'>Sem responsável</span>
        )}
        {assignees.length > 0 && (
          <AvatarGroup
            users={assignees.map((u) => ({name: u!.name, color: u!.avatarColor}))}
            max={2}
            size='sm'
          />
        )}
      </div>
      {board && (
        <div
          className='mt-3 border-t border-slate-100 pt-2'
          onClick={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <select
            aria-label={`Mover tarefa ${task.title} para outra etapa`}
            value={task.status}
            disabled={moving}
            className='w-full rounded-lg border border-transparent bg-slate-50 px-2 py-1.5 text-xs text-slate-600 hover:border-slate-200 disabled:opacity-50'
            onChange={async (event) => {
              if (movingRef.current) return
              movingRef.current = true
              setMoving(true)
              setMoveError('')
              try {
                await apiMoveTask(task.id, event.target.value)
              } catch {
                setMoveError('Não foi possível mover. Tente novamente.')
              } finally {
                movingRef.current = false
                setMoving(false)
              }
            }}
          >
            {!board.columns.some((column) => column.status === task.status) && (
              <option value={task.status}>Etapa não configurada</option>
            )}
            {board.columns.map((column) => (
              <option key={column.id} value={column.status}>
                {column.title}
              </option>
            ))}
          </select>
          {moving && (
            <p role='status' className='mt-1 text-xs text-brand-600'>
              Movendo tarefa...
            </p>
          )}
          {moveError && (
            <p role='alert' className='mt-1 text-xs text-red-600'>
              {moveError}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
